import { keepPreviousData, Updater, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AppID, Filter } from '@playq/octopus-common';
import {
  QuerySynapseProblemsResponse,
  SynapseProblemBrief,
  SynapseProblemID,
  SynapseProblemPromotions,
  SynapseProblemSort,
} from '@playq/octopus2-ml';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import { IEntityPromotion } from '/api/hooks/promotions';
import {
  IUseQueryOptionsExtended,
  useEitherQuery,
  useMutateQueryDataAfterEntityDeleting,
  useQueryPrefetch,
} from '/api/service-hooks';
import { services2 } from '/api/services2';
import { appToolkit } from '/store';

import { problemsQueryKeys } from './constants';
import { createNewProblemsResponseAfterProblemDeleting, mutateEnvsAndFeaturesAfterProblemDeleting } from './helpers';

const emptyArray: [] = [];

export const useProblemsQuery = (
  iterator: OffsetLimit,
  sortBy: SynapseProblemSort[],
  filterBy: {
    [key: string]: Filter;
  },
  options?: IUseQueryOptionsExtended<QuerySynapseProblemsResponse | undefined, GenericFailure | Error>
) => {
  const appID = useSelector(appToolkit.selectors.appID);

  const queryClient = useQueryClient();

  const keys = useMemo(
    () => problemsQueryKeys.concat(appID, iterator, sortBy, filterBy),
    [appID, filterBy, iterator, sortBy]
  );

  const isValid = useMemo(() => appID !== undefined, [appID]);
  const enabled = useMemo(() => options?.enabled !== false && isValid, [options?.enabled, isValid]);
  const enablePrefetch = useMemo(
    () => options?.enablePrefetch !== false && isValid,
    [options?.enablePrefetch, isValid]
  );

  const { mutate, refetch, ...res } = useEitherQuery(
    keys,
    () => services2.synapseProblemsService.queryProblems(appID as AppID, iterator, sortBy, filterBy),
    {
      placeholderData: keepPreviousData,
      ...options,
      enabled,
    }
  );

  const total = useMemo(() => res.data?.total ?? 0, [res.data?.total]);

  const { nextKeys } = useQueryPrefetch({
    keys,
    enabled: enablePrefetch,
    total,
    args: [appID as AppID, iterator, sortBy, filterBy],
    serviceMethod: services2.synapseProblemsService.queryProblems.bind(services2.synapseProblemsService),
  });

  const mutateUpdatePromotions = useCallback(
    (promotions: Updater<IEntityPromotion<SynapseProblemID>[], IEntityPromotion<SynapseProblemID>[]>) => {
      mutate((prevResponse) => {
        if (!prevResponse) {
          refetch();
          return;
        }
        const response = new QuerySynapseProblemsResponse(prevResponse.serialize());
        response.problems = prevResponse.problems;
        response.iterator = prevResponse.iterator;
        response.features = prevResponse.features;

        const nextPromtions = typeof promotions === 'function' ? promotions(response.promotions) : promotions;
        response.promotions = nextPromtions.map(
          (p) => new SynapseProblemPromotions({ id: p.id.serialize(), promotions: p.promotions.serialize() })
        );
        return response;
      });
    },
    [mutate, refetch]
  );

  const mutateDeletedProblem = useMutateQueryDataAfterEntityDeleting({
    queryClient,
    nextPageQueryKey: nextKeys,
    total,
    iterator,
    mutate,
    refetch,
    getID: (p?: SynapseProblemBrief) => p?.id,
    getEntities: (r: QuerySynapseProblemsResponse) => r.problems,
    setEntities: (r: QuerySynapseProblemsResponse, newEntities: SynapseProblemBrief[]) => {
      r.problems = newEntities;
    },
    createNewResponse: createNewProblemsResponseAfterProblemDeleting,
    mutateCustomFields: mutateEnvsAndFeaturesAfterProblemDeleting,
  });

  return {
    ...res,
    problems: res.data?.problems || emptyArray,
    promotions: (res.data?.promotions || emptyArray) as IEntityPromotion<SynapseProblemID>[],
    total,
    tags: keys,
    mutateUpdatePromotions,
    mutateDeletedProblem,
    refetch,
  };
};
